import React, { type FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { CloudArrowUpIn, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ApiError, quickAppUploadApk } from 'client/api';
import { SmartScreenIcon } from 'client/blocks-redesign/icons/SmartScreenIcon';
import { TvIcon } from 'client/blocks-redesign/icons/TvIcon';
import { formatBytes } from 'client/lib/utils';
import { TQuickAppsMeta } from 'client/model/privateApps';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/PrivateApps';

import styles from './QuickAppUploader.module.scss';

interface Props {
    onAdd: (channel: Channel) => void;
    meta: TQuickAppsMeta | undefined;
}

const QUICK_APP_UPLOADER_TOAST = 'QUICK_APP_UPLOADER_TOAST';
const MAX_SIZE_PER_APP_DEFAULT = 524288000;
const TV_CHANNEL_FOR_QUICK_APPS_IS_DISABLED = true;

const t = i18n(keyset);

export const QuickAppUploader: FC<Props> = ({ onAdd, meta }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [file, setFile] = useState<File>();

    const { add: addToast, remove: removeToast } = useToaster();

    const maxSize = formatBytes(meta?.max_size_per_app_b || MAX_SIZE_PER_APP_DEFAULT);

    const handleAppUpload = useCallback(
        (selectedChannel: Channel, file?: File) => {
            if (!selectedChannel || !file) return;

            setIsLoading(true);
            setFile(undefined);
            removeToast(QUICK_APP_UPLOADER_TOAST);

            quickAppUploadApk(selectedChannel, file)
                .then((_uploadedAppId) => {
                    onAdd(selectedChannel);
                })
                .catch((error) => {
                    let errorMessage = error.message;

                    if (error instanceof ApiError && error.fields) {
                        errorMessage = Object.values(error.fields).join(', ');
                    }

                    addToast({
                        title: t('error'),
                        theme: 'danger',
                        content: errorMessage,
                        name: QUICK_APP_UPLOADER_TOAST,
                        autoHiding: false,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [onAdd, addToast, removeToast],
    );

    const onDrop = useCallback((acceptedFiles) => {
        setFile(acceptedFiles[0]);
    }, []);

    const onChannelSelect = useCallback(
        (channel: Channel) => {
            handleAppUpload(channel, file);
        },
        [file, handleAppUpload],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: isLoading,
        accept: ['.apk'],
    });

    return (
        <>
            <div
                className={clx(
                    styles.quickAppUploader__container,
                    isDragActive && styles.quickAppUploader__container_dragActive,
                )}
                {...getRootProps()}
            >
                <div className={styles.quickAppUploader__content}>
                    {!isDragActive && <CloudArrowUpIn className={styles.quickAppUploader__icon} />}
                    <h3 className={styles.quickAppUploader__title}>{t('upload-apk-files-hint')}</h3>
                    {!isDragActive && (
                        <div className={styles.quickAppUploader__description}>{t('max-file-size', { maxSize })}</div>
                    )}
                    <input {...getInputProps()} />
                    {!isDragActive && <Button loading={isLoading}>{t('upload')}</Button>}
                </div>
            </div>
            <Modal open={!!file} onClose={() => setFile(undefined)}>
                <div className={styles.quickAppChannelModal__container}>
                    <header className={styles.quickAppChannelModal__header}>
                        <h4 className={styles.quickAppChannelModal__title}>{t('select-device-question')}</h4>
                        <Button
                            className={styles.quickAppChannelModal__closeButton}
                            onClick={() => setFile(undefined)}
                            view="flat"
                        >
                            <Icon data={Xmark} />
                        </Button>
                    </header>
                    <div className={styles.quickAppChannelModal__text}>{t('device-info')}</div>
                    <div className={styles.quickAppChannelModal__buttonsContainer}>
                        <Button
                            className={styles.quickAppChannelModal__button}
                            onClick={() => onChannelSelect(Channel.SmartScreenApp)}
                            size="l"
                        >
                            <Icon data={SmartScreenIcon} />
                            {t('device-station-duo-max')}
                        </Button>
                        <Button
                            className={styles.quickAppChannelModal__button}
                            onClick={() => onChannelSelect(Channel.TvApp)}
                            size="l"
                            disabled={TV_CHANNEL_FOR_QUICK_APPS_IS_DISABLED}
                        >
                            <Icon data={TvIcon} />
                            {t('device-tv-with-yaos')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
